import './MissionDisplay.css';

import React, { useCallback, useEffect, useState } from 'react';
import { Accordion as AccordionBootstrap, Button, Card, Form, Spinner, Table } from 'react-bootstrap';
import moment from 'moment';
import { useMutation } from '@apollo/client';

import {
  AccordionProps,
  AchievementMissionsDisplayProps,
  AchievementMissionsProgressResultProps,
  Mission,
} from '../typedefs';
import { FORCE_MISSION_PROGRESS, RESET_ACHIEVEMENT_PROGRESS } from '../../../graphql';

const AchievementMissionsDisplay: React.FC<AchievementMissionsDisplayProps> = ({
  userId,
  missions,
  missionSetUserId,
}) => {
  const [expanded, setExpanded] = useState(false);

  const [resetForUser, { loading: resetUserLoading }] = useMutation(RESET_ACHIEVEMENT_PROGRESS);

  const onResetThisUser = useCallback(() => {
    if (!missionSetUserId) {
      return;
    }
    if (!window.confirm(`Reset ALL achievement progress for user ${userId}? Test/alpha only.`)) {
      return;
    }
    resetForUser({ variables: { input: { missionSetUserId } } });
  }, [resetForUser, missionSetUserId, userId]);

  return (
    <Card className="mb-2 shadow-sm border-0">
      <Card.Body className="py-2">
        <AccordionBootstrap>
          <AccordionBootstrap.Toggle as={Card.Title} variant="link" eventKey="0" onClick={() => setExpanded(!expanded)}>
            <div style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
              Achievement missions
              <Button variant="link">
                {!expanded && <i className="fas fa-chevron-down" />}
                {expanded && <i className="fas fa-chevron-up" />}
              </Button>
            </div>
          </AccordionBootstrap.Toggle>
          <AccordionBootstrap.Collapse eventKey="0">
            <>
              {missions ? (
                <>
                  <div className="mt-2">
                    <Button
                      variant="warning"
                      size="sm"
                      disabled={resetUserLoading || !missionSetUserId}
                      onClick={onResetThisUser}>
                      {resetUserLoading ? <Spinner animation="border" size="sm" /> : 'Reset for this user'}
                    </Button>
                  </div>
                  <div className="mt-2" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                    <MissionProgressResults missions={missions} loading={false} />
                  </div>
                </>
              ) : (
                'No missions have been configured for this monthly pass'
              )}
            </>
          </AccordionBootstrap.Collapse>
        </AccordionBootstrap>
      </Card.Body>
    </Card>
  );
};

export default AchievementMissionsDisplay;

const MissionProgressResults: React.FC<AchievementMissionsProgressResultProps> = ({ missions, loading }) => {
  const displayEmptyArray = () => {
    return <div>Empty Array</div>;
  };

  const displayMissions = (data: Mission[]) => {
    return (
      <Table striped bordered hover size="sm">
        <thead>
          <tr>
            <th>Task name</th>
            <th>Reward coins</th>
            <th>Reward diamonds</th>
            <th style={{ minWidth: 180 }}>Progress</th>
            <th>Claimed date</th>
            <th>Claimed multiplier</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.map((mission: Mission) => (
            <ProgressRow key={mission.id} mission={mission} />
          ))}
        </tbody>
      </Table>
    );
  };

  if (!missions) {
    return <div>No result</div>;
  }

  return <div>{missions.length > 0 ? displayMissions(missions) : displayEmptyArray()}</div>;
};

const ProgressRow: React.FC<{ mission: Mission }> = ({ mission }) => {
  const goal = mission.progressGoal ?? 0;
  const currentProgress = mission.progress ?? 0;
  const isCompleted = !!mission.completedDate;
  const [value, setValue] = useState<string>(String(currentProgress));
  const [pendingAction, setPendingAction] = useState<'save' | 'complete' | null>(null);

  // Re-sync local input when the BE returns a fresh value (after Save / Complete).
  useEffect(() => {
    setValue(String(currentProgress));
  }, [currentProgress]);

  const [forceProgress] = useMutation(FORCE_MISSION_PROGRESS);

  const parsed = parseInt(value, 10);
  const isValid = !Number.isNaN(parsed) && parsed >= 0 && parsed <= goal;
  const isDirty = isValid && parsed !== currentProgress;
  const isBusy = pendingAction !== null;

  const save = useCallback(
    (nextProgress: number, action: 'save' | 'complete') => {
      if (!mission.id) return;
      setPendingAction(action);
      forceProgress({
        variables: { input: { missionProgressId: mission.id, progress: nextProgress } },
      }).finally(() => setPendingAction(null));
    },
    [forceProgress, mission.id],
  );

  const onSave = () => isValid && save(parsed, 'save');
  const onComplete = () => {
    const taskName = mission?.mission?.tasks[0]?.type ?? 'this achievement';
    if (!window.confirm(`Mark "${taskName}" as complete for this user?`)) {
      return;
    }
    save(goal, 'complete');
  };

  const saveDisabled = !isDirty || isBusy || isCompleted;
  const completeDisabled = isBusy || isCompleted || currentProgress >= goal;

  return (
    <tr>
      <td>{mission?.mission?.tasks[0]?.type}</td>
      <td>{mission?.mission?.rewardCoins}</td>
      <td>{mission?.mission?.rewardDiamonds}</td>
      <td>
        <div className="d-flex align-items-center" style={{ gap: 4 }}>
          <Form.Control
            type="number"
            size="sm"
            min={0}
            max={goal}
            value={value}
            disabled={isBusy || isCompleted}
            onChange={(e) => setValue(e.target.value)}
            style={{ width: 70 }}
            isInvalid={!isValid}
          />
          <span>/ {goal}</span>
          {isCompleted && (
            <span
              style={{ fontSize: 11, color: '#198754', fontWeight: 600 }}
              title="Already completed. To fully reset use 'Reset for this user'.">
              ✓ completed
            </span>
          )}
        </div>
      </td>
      <td>
        {mission?.claimedDate
          ? moment.unix(mission.claimedDate / 1000).format('MM/DD/YYYY HH:mm:ss')
          : 'not available'}
      </td>
      <td>{mission?.claimedMultiplier ? mission.claimedMultiplier : 'not available'}</td>
      <td>
        <div className="d-flex" style={{ gap: 4 }}>
          <Button
            size="sm"
            variant={saveDisabled ? 'secondary' : 'primary'}
            disabled={saveDisabled}
            title={isCompleted ? 'Already completed — use Reset for this user to clear' : undefined}
            onClick={onSave}>
            {pendingAction === 'save' ? <Spinner animation="border" size="sm" /> : 'Save'}
          </Button>
          <Button
            size="sm"
            variant="success"
            disabled={completeDisabled}
            onClick={onComplete}>
            {pendingAction === 'complete' ? (
              <Spinner animation="border" size="sm" />
            ) : isCompleted || currentProgress >= goal ? (
              'Completed'
            ) : (
              'Complete'
            )}
          </Button>
        </div>
      </td>
    </tr>
  );
};

const Accordion: React.FC<AccordionProps> = ({ children, active }) => {
  return <div className={`mission_pannel_collapse ${active ? 'active' : ''}`}>{children}</div>;
};
